"use client"
import { Modal } from "@/components/ui-library-new/modal/Modal"
import { Button } from "@/components/ui-library/button/Button"
import LeftArrow from "@/layout/icons/LeftArrow"
import SpinnerIcon from "@/layout/icons/SpinnerIcon"
import { useConfigStore } from "@/store/config-store"
import { cn } from "@/utils/utils"
import { useState } from "react"

type IframeModalProps = {
  /** External URL loaded as the iframe `src`. */
  src: string
  onClose: () => void
  useCloseButton?: boolean
  modalClassName?: string
}

// Written out in full on purpose: Tailwind scans source text, so a class assembled from a template
// literal is never generated. `fine:min-[1025px]:` has to appear verbatim to reach the stylesheet.
/** Full-bleed by default; a centered card only for a real pointer on a wide screen. */
const PANEL = "h-full w-full fine:min-[1025px]:h-5/6 fine:min-[1025px]:w-5/6"
/** Only present once the card treatment is active. */
const CARD_ONLY = "hidden fine:min-[1025px]:flex"
/** Drops away when the card treatment takes over. */
const TOUCH_ONLY = "fine:min-[1025px]:hidden"

export const IframeModal = ({ src, onClose, useCloseButton = true, modalClassName }: IframeModalProps) => {
  // The iframe is always mounted, with the spinner stacked over it until `load` fires. Rendering it
  // only after a timer meant `onLoad` could never be what revealed it.
  const [loaded, setLoaded] = useState(false)
  const strings = useConfigStore.getState().strings

  return (
    <Modal
      panelClassName={PANEL}
      closeButtonClassName={CARD_ONLY}
      useCloseButton={useCloseButton}
      onClose={onClose}
      className={cn("rounded-none p-0 fine:min-[1025px]:rounded-b-lg", modalClassName)}
    >
      {/* One structure for both treatments. Nothing overlays the iframe — that breaks cross-origin
          touch delivery — so the spinner is a pointer-events-none sibling inside the frame's own
          wrapper, and the button is a plain flex sibling below it. */}
      <div className="flex h-full w-full flex-col">
        <div className="relative h-full min-h-0 w-full">
          <iframe
            src={src}
            title="Embedded Content"
            className={cn(
              "h-full w-full border-none transition-opacity duration-300",
              loaded ? "opacity-100" : "opacity-0"
            )}
            onLoad={() => setLoaded(true)}
          />
          <div
            aria-hidden={loaded}
            className={cn(
              "pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300",
              loaded && "opacity-0"
            )}
          >
            <SpinnerIcon className="h-auto w-36 animate-spin fine:min-[1025px]:w-40" />
          </div>
        </div>

        {useCloseButton && (
          <Button
            onClick={onClose}
            className={cn(
              "flex h-14 w-full shrink-0 items-center justify-center rounded-none text-base",
              TOUCH_ONLY,
              !loaded && "opacity-0"
            )}
          >
            <LeftArrow /> &nbsp; {strings?.button.goBack}
          </Button>
        )}
      </div>
    </Modal>
  )
}

export default IframeModal
